import * as THREE from 'three'
import { CSS2DRenderer } from 'three/examples/jsm/renderers/CSS2DRenderer.js'

const FRUSTUM_SIZE = 60

export class SceneEngine {
  scene: THREE.Scene
  camera: THREE.OrthographicCamera
  renderer: THREE.WebGLRenderer
  labelRenderer: CSS2DRenderer
  private animationId: number | null = null
  private disposed = false

  constructor(canvas: HTMLCanvasElement) {
    // Scene
    this.scene = new THREE.Scene()
    this.scene.background = new THREE.Color(0x1a1a2e)

    // Camera
    const aspect = canvas.clientWidth / canvas.clientHeight
    this.camera = new THREE.OrthographicCamera(
      (-FRUSTUM_SIZE * aspect) / 2,
      (FRUSTUM_SIZE * aspect) / 2,
      FRUSTUM_SIZE / 2,
      -FRUSTUM_SIZE / 2,
      0.1,
      1000
    )
    this.camera.position.set(50, 50, 50)
    this.camera.lookAt(0, 15, 0)

    // Renderer
    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: true })
    this.renderer.shadowMap.enabled = true
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap
    this.renderer.setSize(canvas.clientWidth, canvas.clientHeight)
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))

    // CSS2D Label Renderer
    this.labelRenderer = new CSS2DRenderer()
    this.labelRenderer.setSize(canvas.clientWidth, canvas.clientHeight)
    this.labelRenderer.domElement.style.position = 'absolute'
    this.labelRenderer.domElement.style.top = '0'
    this.labelRenderer.domElement.style.left = '0'
    this.labelRenderer.domElement.style.pointerEvents = 'none'
    canvas.parentElement?.appendChild(this.labelRenderer.domElement)

    // Lighting
    const ambient = new THREE.AmbientLight(0xffffff, 0.6)
    this.scene.add(ambient)

    const directional = new THREE.DirectionalLight(0xffffff, 0.8)
    directional.position.set(30, 50, 30)
    directional.castShadow = true
    directional.shadow.mapSize.set(2048, 2048)
    directional.shadow.camera.left = -50
    directional.shadow.camera.right = 50
    directional.shadow.camera.top = 50
    directional.shadow.camera.bottom = -50
    directional.shadow.camera.far = 200
    this.scene.add(directional)
  }

  animate(callback?: () => void) {
    if (this.disposed) return
    this.animationId = requestAnimationFrame(() => this.animate(callback))
    callback?.()
    this.renderer.render(this.scene, this.camera)
    this.labelRenderer.render(this.scene, this.camera)
  }

  resize(width: number, height: number) {
    if (width === 0 || height === 0) return
    const aspect = width / height
    this.camera.left = (-FRUSTUM_SIZE * aspect) / 2
    this.camera.right = (FRUSTUM_SIZE * aspect) / 2
    this.camera.top = FRUSTUM_SIZE / 2
    this.camera.bottom = -FRUSTUM_SIZE / 2
    this.camera.updateProjectionMatrix()
    this.renderer.setSize(width, height)
    this.labelRenderer.setSize(width, height)
  }

  dispose() {
    this.disposed = true
    if (this.animationId !== null) {
      cancelAnimationFrame(this.animationId)
    }
    this.renderer.dispose()
    this.labelRenderer.domElement.remove()
    this.scene.traverse((obj) => {
      if (obj instanceof THREE.Mesh) {
        obj.geometry.dispose()
        if (Array.isArray(obj.material)) {
          obj.material.forEach((m) => m.dispose())
        } else {
          obj.material.dispose()
        }
      }
    })
  }
}
